import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MainTemplate } from 'templates';
import { LoadingIndicator } from 'atoms';
import {
  useAllFakturowniaInvoices,
  IInvoicesFilters,
  IFakturowniaInvoiceListRow,
  usePopupStateOperations,
} from 'core/hooks';
import { PopupNames } from 'core';
import {
  DateRangePresets,
  FakturowniaSyncChip,
  IDateRangeValue,
  ILinkInvoicePaymentPopupData,
  presetRange,
} from 'molecules';

import * as S from './FakturowniaInvoices.styled';
import { InvoicesFilterBar, InvoicesStateChip } from './InvoicesFilterBar';
import { InvoicesSummary } from './InvoicesSummary';
import { InvoicesTable } from './InvoicesTable';

/**
 * Top-level Invoices page (TZ §3.2). Three responsibilities only:
 *   1. Own the filter state and pass it through to `useFakturowniaInvoices`.
 *   2. Render the sync chip + unmatched badge + filter ribbon + table.
 *   3. Wire the inline «Link student» action into the Match popup.
 * Everything else (data, mutations, polling) is encapsulated in hooks.
 *
 * Phase 6: defaults to the last 3 months (fixes the «opened the page and
 * see nothing» bug — the old default was unbounded and looked empty when
 * the recent slice was small) and exposes shared date presets above the
 * filter ribbon.
 */
const PHASE6_DEFAULT_RANGE: IDateRangeValue = {
  preset: 'last3Months',
  ...presetRange('last3Months'),
};

const buildDefaultFilters = (
  range: IDateRangeValue = PHASE6_DEFAULT_RANGE,
): IInvoicesFilters => ({
  direction: 'issued',
  dateFrom: range.from,
  dateTo: range.to,
});

export const FakturowniaInvoices = memo(() => {
  const { t } = useTranslation();
  const {
    operations: { showPopup },
  } = usePopupStateOperations();

  const [range, setRange] = useState<IDateRangeValue>(PHASE6_DEFAULT_RANGE);
  const [filters, setFilters] = useState<IInvoicesFilters>(() =>
    buildDefaultFilters(),
  );
  const [searchQuery, setSearchQuery] = useState('');
  // Phase 8: default ON — financier wanted a clean view by first opening,
  // toggle OFF to inspect corrections + their originals together.
  const [hideCorrected, setHideCorrected] = useState(true);
  // Phase 10: payment-state chip — translates into backend filters
  // (status / overdueOnly) plus one client-side case ('unpaid' = netting).
  const [stateChip, setStateChip] = useState<InvoicesStateChip>('all');

  // Debounce search → filters.search so each keystroke doesn't fire a query.
  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((prev) =>
        (prev.search || '') === searchQuery.trim()
          ? prev
          : { ...prev, search: searchQuery.trim() || undefined },
      );
    }, 300);
    return () => clearTimeout(handle);
  }, [searchQuery]);

  const onRangeChange = useCallback((next: IDateRangeValue) => {
    setRange(next);
    setFilters((prev) => ({
      ...prev,
      dateFrom: next.from,
      dateTo: next.to,
    }));
  }, []);

  // Phase 13: every chip now hits a real backend filter — `reconcile` uses
  // the new `unpaidOnly:true` arg (Payment IS NULL), replacing the old
  // client-side `netOutstanding > 0` heuristic. The two semantics differ:
  // reconcile == «no LMS payment exists at all», unpaid == «balance > 0
  // after corrections». Finance staff cares about the former first.
  const stateBackendFilters = useMemo<Partial<IInvoicesFilters>>(() => {
    switch (stateChip) {
      case 'reconcile':
        return {
          status: undefined,
          overdueOnly: undefined,
          unpaidOnly: true,
        };
      case 'paid':
        return {
          status: 'paid',
          overdueOnly: undefined,
          unpaidOnly: undefined,
        };
      case 'overdue':
        return {
          status: undefined,
          overdueOnly: true,
          unpaidOnly: undefined,
        };
      case 'rejected':
        return {
          status: 'rejected',
          overdueOnly: undefined,
          unpaidOnly: undefined,
        };
      default:
        return {
          status: undefined,
          overdueOnly: undefined,
          unpaidOnly: undefined,
        };
    }
  }, [stateChip]);

  const effectiveFilters = useMemo<IInvoicesFilters>(
    () => ({ ...filters, ...stateBackendFilters }),
    [filters, stateBackendFilters],
  );

  const {
    data,
    loading,
    fetching,
    permissionDenied,
    refetch,
  } = useAllFakturowniaInvoices(effectiveFilters);

  const onSynced = useCallback(() => {
    refetch();
  }, [refetch]);

  const onReset = useCallback(() => {
    setRange(PHASE6_DEFAULT_RANGE);
    setFilters(buildDefaultFilters());
    setSearchQuery('');
    setStateChip('all');
  }, []);

  // Phase 13: inline button on the reconciliation queue opens the new
  // payment-aware popup in `link` mode (most common — there's usually a
  // matching LMS Payment, finance just needs to pick it). The detail page
  // exposes the «create payment from invoice» fallback for the rare case.
  const onLinkRow = useCallback(
    (row: IFakturowniaInvoiceListRow) => {
      const payload: ILinkInvoicePaymentPopupData = {
        invoiceId: row.id,
        mode: 'link',
        buyerEmail: row.buyerEmail || undefined,
        prefillStudentId: row.linkedStudent?.id || undefined,
        onLinked: () => refetch(),
      };
      showPopup(PopupNames.linkInvoicePayment, payload);
    },
    [showPopup, refetch],
  );

  // Phase 8: client-side hide of corrections AND their corrected parents.
  // The toggle is UI-only, not a GraphQL arg — backend still returns the
  // raw set so other consumers (CorrectionsList, breakdown «Корректировки»
  // tab) keep working. Phase 13: the «unpaid» client filter dropped — the
  // new `reconcile` chip is server-side via `unpaidOnly:true`.
  const visibleData = useMemo(
    () =>
      hideCorrected
        ? data.filter(
            (r) =>
              !r.isCorrection && !(r.corrections && r.corrections.length > 0),
          )
        : data,
    [data, hideCorrected],
  );

  if (permissionDenied) {
    return (
      <MainTemplate>
        <S.Wrapper>
          <S.PageTitle>{t('pages.fakturownia.invoices.title')}</S.PageTitle>
          <S.EmptyState>
            {t('pages.fakturownia.invoices.noAccess')}
          </S.EmptyState>
        </S.Wrapper>
      </MainTemplate>
    );
  }

  if (loading && data.length === 0) {
    return (
      <MainTemplate>
        <S.Wrapper>
          <S.CenterLoader>
            <LoadingIndicator />
          </S.CenterLoader>
        </S.Wrapper>
      </MainTemplate>
    );
  }

  const isReconcileQueue = stateChip === 'reconcile';
  const isEmpty = visibleData.length === 0;

  return (
    <MainTemplate>
      <S.Wrapper>
        <S.HeaderRow>
          <S.PageTitle>{t('pages.fakturownia.invoices.title')}</S.PageTitle>
          <S.HeaderRight>
            <FakturowniaSyncChip onSynced={onSynced} />
          </S.HeaderRight>
        </S.HeaderRow>
        <S.SubTitle>{t('pages.fakturownia.invoices.subtitle')}</S.SubTitle>

        <DateRangePresets value={range} onChange={onRangeChange} />

        <InvoicesFilterBar
          filters={filters}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onChange={setFilters}
          onReset={onReset}
          hideCorrected={hideCorrected}
          onHideCorrectedChange={setHideCorrected}
          state={stateChip}
          onStateChange={setStateChip}
        />

        {isReconcileQueue && !isEmpty ? (
          <S.Banner variant="warn">
            <S.BannerTitle>
              {t('pages.fakturownia.invoices.reconcileBannerTitle', {
                count: visibleData.length,
              })}
            </S.BannerTitle>
            {t('pages.fakturownia.invoices.reconcileBannerHint')}
          </S.Banner>
        ) : null}

        {isEmpty && isReconcileQueue ? (
          <S.Banner variant="success">
            {t('pages.fakturownia.invoices.allReconciled')}
          </S.Banner>
        ) : null}

        {isEmpty && !isReconcileQueue ? (
          <S.EmptyState>
            <div>{t('pages.fakturownia.invoices.empty')}</div>
            <S.EmptyHint>
              {t('pages.fakturownia.invoices.emptyHint', {
                from: filters.dateFrom || '—',
                to: filters.dateTo || '—',
              })}
            </S.EmptyHint>
            <S.InlineButton
              type="button"
              onClick={onReset}
              style={{ marginTop: 12 }}
            >
              {t('pages.fakturownia.invoices.clearFilters')}
            </S.InlineButton>
          </S.EmptyState>
        ) : (
          <S.DimWhenFetching fetching={fetching}>
            <InvoicesSummary rows={visibleData} />
            <InvoicesTable rows={visibleData} onLinkRow={onLinkRow} />
          </S.DimWhenFetching>
        )}
      </S.Wrapper>
    </MainTemplate>
  );
});
